import {
    FETCH_MESSAGES,
    FETCH_MESSAGES_SUCCESS,
    FETCH_MESSAGES_FAILED,
    SEND_MESSAGE,
    STOP_FETCH_MESSAGES
} from "../store/types";
import store from '../store/store';
import { firebase } from '../config/configureFirebase';
import { RequestPushMsg, isExpoPushToken } from '../other/NotificationFunctions';
import { onValue, off, get, child, ref as dbRef } from "firebase/database";
import { safePush, safeRead, safeUpdate } from '../other/firebaseDbHelper';

const chatPollers = {};

const getChatMessagesRef = (bookingId, chatId) => {
    let messagesRef = null;

    if (typeof firebase?.chatRef === 'function') {
        messagesRef = firebase.chatRef(bookingId);
    } else if (firebase?.database) {
        messagesRef = dbRef(firebase.database, 'chats/' + bookingId + '/messages');
    }

    return messagesRef && chatId ? child(messagesRef, chatId) : messagesRef;
};

const normalizeMessages = (conversation, chatId) => {
    if (!conversation) {
        return null;
    }

    const allMesseges = [];
    for (let msgKey in conversation) {
        allMesseges.push({
            ...conversation[msgKey],
            smsId: msgKey,
            chatId,
        });
    }

    return allMesseges;
};

const dispatchMessages = (dispatch, conversation, chatId) => {
    const allMesseges = normalizeMessages(conversation, chatId);

    if (allMesseges) {
        dispatch({
            type: FETCH_MESSAGES_SUCCESS,
            payload: allMesseges,
        });
    } else {
        dispatch({
            type: FETCH_MESSAGES_FAILED,
            payload: store.getState().languagedata.defaultLanguage.chat_not_found,
        });
    }
};

const getChatPollerKey = (bookingId, chatId) => `${bookingId}:${chatId || ''}`;

const clearChatPolling = (bookingId, chatId) => {
    const pollerKey = getChatPollerKey(bookingId, chatId);
    if (chatPollers[pollerKey]) {
        clearInterval(chatPollers[pollerKey]);
        delete chatPollers[pollerKey];
    }
};

const startChatPolling = (bookingId, chatId, dispatch) => {
    const pollerKey = getChatPollerKey(bookingId, chatId);
    clearChatPolling(bookingId, chatId);

    const fetchViaRest = () => {
        safeRead(`/chats/${bookingId}/messages/${chatId}`)
            .then((data) => {
                dispatchMessages(dispatch, data, chatId);
            })
            .catch(() => {
                dispatch({
                    type: FETCH_MESSAGES_FAILED,
                    payload: store.getState().languagedata.defaultLanguage.chat_not_found,
                });
            });
    };

    fetchViaRest();
    chatPollers[pollerKey] = setInterval(fetchViaRest, 2000);
};

const getLiveRecipientToken = async (uid) => {
    const { singleUserRef } = firebase;

    if (!uid) {
        return null;
    }

    try {
        const snapshot = await get(singleUserRef(uid));
        const profile = snapshot.val();
        const token = profile?.pushToken;

        if (!isExpoPushToken(token)) {
            return null;
        }

        return token;
    } catch (error) {
        console.error('[chat-push] Failed to fetch live recipient token.', {
            uid,
            error: error?.message || error,
        });
        return null;
    }
};

const sendChatPushNotification = async (data) => {
    const isCustomer = data.role == 'customer';
    const bookingRecipientToken = isCustomer ? data.booking?.driver_token : data.booking?.customer_token;
    const recipientId = isCustomer ? data.booking?.driver : data.booking?.customer;
    const recipientLabel = isCustomer ? 'driver' : 'customer';

    let tokenToUse = await getLiveRecipientToken(recipientId);

    if (!tokenToUse) {
        if (isExpoPushToken(bookingRecipientToken)) {
            tokenToUse = bookingRecipientToken;
        }
    }

    if (!tokenToUse) {
        console.warn(`[chat-push] Missing valid ${recipientLabel} token for chat notification.`, {
            bookingId: data.booking?.id,
            senderRole: data.role,
            driverId: data.booking?.driver,
            customerId: data.booking?.customer,
            bookingTokenPresent: !!bookingRecipientToken,
        });
        return;
    }

    const result = await RequestPushMsg(
        tokenToUse,
        {
            title: store.getState().languagedata.defaultLanguage.notification_title + store.getState().languagedata.defaultLanguage.chat_requested,
            msg: data.message,
            screen: 'onlineChat',
            params: { bookingId: data.booking.id }
        }
    );

    if (!result?.ok) {
        console.warn('[chat-push] Notification request did not report success.', {
            bookingId: data.booking?.id,
            senderRole: data.role,
            recipientId,
            recipientLabel,
            result,
        });
    }
};

export const fetchChatMessages = (bookingId, chatId) => (dispatch) => {
    const messagesRef = getChatMessagesRef(bookingId, chatId);
  
    dispatch({
        type: FETCH_MESSAGES,
        payload: bookingId,
    });

    if (!messagesRef || !chatId) {
        dispatch({
            type: FETCH_MESSAGES_FAILED,
            payload: store.getState().languagedata.defaultLanguage.chat_not_found,
        });
        return;
    }

    if (!firebase?.auth?.currentUser) {
        startChatPolling(bookingId, chatId, dispatch);
        return;
    }

    clearChatPolling(bookingId, chatId);

    onValue(
        messagesRef,
        (snapshot) => {
            dispatchMessages(dispatch, snapshot.val(), chatId);
        },
        () => {
            dispatch({
                type: FETCH_MESSAGES_FAILED,
                payload: store.getState().languagedata.defaultLanguage.chat_not_found,
            });
        }
    );
};

export const sendMessage = (data) => (dispatch) => {
    const messagesRef = getChatMessagesRef(data.booking.id);
    const useRealtimeAuth = !!firebase?.auth?.currentUser;
    console.log('[chat-send] sendMessage called.', {
        bookingId: data.booking?.id || null,
        role: data.role || null,
        customerId: data.booking?.customer || null,
        driverId: data.booking?.driver || null,
        messageLength: data.message?.length || 0,
        hasMessagesRef: !!messagesRef,
        useRealtimeAuth,
    });

    if (!data.booking?.customer || !data.booking?.driver) {
        console.warn('[chat-send] Message send skipped because the booking has no active participants.', {
            bookingId: data.booking?.id || null,
            customerId: data.booking?.customer || null,
            driverId: data.booking?.driver || null,
        });
        return;
    }

    const chatId = data.booking.customer + ','  + data.booking.driver
    var today = new Date();
    var time = today.getHours() + ":" + today.getMinutes();
    var dd = String(today.getDate()).padStart(2, '0');
    var mm = String(today.getMonth() + 1).padStart(2, '0'); 
    var yyyy = today.getFullYear();
    today = mm + ':' + dd + ':' + yyyy;
    
    const msg = {
        message:data.message,
        from: data.role == 'customer'? data.booking.customer : data.booking.driver,
        type:"msg",
        msgDate:today,
        msgTime:time,
        createdAt: new Date().toString(),
        source: data.role,
        readBy: {
            [data.role == 'customer' ? data.booking.customer : data.booking.driver]: new Date().getTime(),
        },
    };

    if (!messagesRef) {
        console.warn('[chat-send] Missing messagesRef. Message send skipped.', {
            bookingId: data.booking?.id || null,
        });
        return;
    }

    safePush(child(messagesRef, chatId), msg);
    console.log('[chat-send] Message pushed to realtime DB.', {
        bookingId: data.booking?.id || null,
        chatId,
    });
    sendChatPushNotification(data).catch((error) => {
        console.error('[chat-push] Failed to send chat notification.', {
            bookingId: data.booking?.id || null,
            error: error?.message || String(error),
        });
    });

    if (!useRealtimeAuth) {
        dispatch({
            type: SEND_MESSAGE,
            payload: msg,
        });
    }
}

export const markChatMessagesRead = (bookingId, chatId, role) => async () => {
    const messagesRef = getChatMessagesRef(bookingId, chatId);
    const uid = store.getState().auth.profile?.uid;

    if (!messagesRef || !bookingId || !chatId || !role || !uid) {
        return;
    }

    let conversation = null;

    try {
        if (firebase?.auth?.currentUser) {
            const snapshot = await get(messagesRef);
            conversation = snapshot.val();
        } else {
            conversation = await safeRead(`/chats/${bookingId}/messages/${chatId}`);
        }
    } catch {
        return;
    }

    if (!conversation) {
        return;
    }

    const readAt = new Date().getTime();
    const updates = [];

    for (let msgKey in conversation) {
        const msg = conversation[msgKey];
        if (msg?.source && msg.source !== role && !msg.readBy?.[uid]) {
            updates.push(safeUpdate(child(messagesRef, `${msgKey}/readBy`), { [uid]: readAt }));
        }
    }

    await Promise.all(updates);
};

export const stopFetchMessages = (bookingId, chatId) => (dispatch) => {
    const messagesRef = getChatMessagesRef(bookingId, chatId);

    dispatch({
        type: STOP_FETCH_MESSAGES,
        payload: bookingId,
    });

    clearChatPolling(bookingId, chatId);

    if (messagesRef && chatId) {
        off(messagesRef);
    }
}
